import type { ToneNotation } from "@kensio/pinyinjs";

import type { CanonicalEntry } from "../segmenting/canonical-entry.js";
import { element } from "./dom.js";
import { hskBandLevel } from "../content/hsk/hsk-band.js";
import { glossCell, hanziNodes, pinyinNodes } from "./segmenter-cells.js";
import type { SegmentedWord } from "../segmenting/segmented-words.js";
import { segmenterSaveControl } from "./segmenter-save-control.js";

/**
 * Drawing the word list.
 *
 * Apart from the tool for the reason `output.ts` is apart from the hanzi tool.
 * What a row looks like changes for editorial reasons and what the tool does
 * changes for behavioural ones. What a reader pastes is built in
 * `segmenter-copy.ts`, out of the same words.
 *
 * **The general frequency band had a column here and no longer does.** How
 * often anybody uses a word is a weaker thing to know than what it means, and
 * the two were competing for the same inches. The band is still an order the
 * list can be read in, under the table, and still a column of what the Copy
 * button puts on the clipboard.
 */

/** What one row needs told. */
export interface WordRowProperties {
  readonly word: SegmentedWord;

  readonly notation: ToneNotation;

  /** Whether the reader already holds this word. */
  readonly held?: boolean | undefined;

  /** What the site says the word means, where this page has been told. */
  readonly entry?: CanonicalEntry | undefined;

  /** Whether the reader has opened this row's meaning. */
  readonly open?: boolean | undefined;

  /** Where the icon sprite is, for the marks the save control draws. */
  readonly icons?: string | undefined;
}

/**
 * One word as a table row.
 *
 * `lang="zh"` on the characters rather than `zh-Hans` or `zh-Hant`. Which
 * script the text is in would take the conversion tables, which is 100 KB this
 * tool fetches for nothing else, and a page that guessed would put the wrong
 * font on half of what a reader pasted. `zh` is true of both.
 */
export function wordRow(properties: WordRowProperties): HTMLTableRowElement {
  const { word, notation } = properties;

  return element("tr", {
    className: "cb-segmenter-row",
    children: [
      element("th", {
        className: "cb-segmenter-hanzi hanzi",
        attributes: { scope: "row", lang: "zh" },
        children: hanziNodes(word),
      }),
      element("td", {
        className: "cb-segmenter-pinyin pinyin",
        attributes: { lang: "zh-Latn-CN-pinyin" },
        children: pinyinNodes(word, notation),
      }),
      glossCell(word, properties.entry, properties.open ?? false),
      element("td", {
        className: "cb-segmenter-count",
        text: String(word.count),
      }),
      /*
       * The level rather than the whole term, under a heading that says HSK.
       * A cell reading `HSK 3` under a column headed `HSK` says it twice, and
       * this column is 750 rows long.
       */
      element("td", {
        className: "cb-segmenter-hsk",
        text: word.band === undefined ? "" : hskBandLevel(word.band),
      }),
      element("td", {
        className: "cb-segmenter-save",
        children: [
          segmenterSaveControl({
            word,
            held: properties.held ?? false,
            icons: properties.icons,
          }),
        ].filter((node) => node !== undefined),
      }),
    ],
  });
}
